import {
  LayoutDashboard,
  ShieldAlert,
  ShieldCheck,
  Scale,
  Users,
  Zap,
  Sparkles,
  Ticket,
  BarChart3,
  Settings,
  ScrollText,
  Archive,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Batch 5: expanded from 6 to 12 domains so every major dashboard section
 * has its own visual identity, per the Batch 5 color-system brief — rather
 * than collapsing AutoMod into Security's red and Moderation/Settings/
 * Logs/Backups all into one shared "management" blue. 'management' is kept
 * for any code not yet migrated to a more specific tone; 'help' exists for
 * completeness (docs/DESIGN_SYSTEM.md's token-naming request) but is
 * deliberately muted/low-saturation rather than a vivid accent — Help/Docs
 * pages are rarely accented at all, and a vivid green/emerald there sat too
 * close to Analytics' green and Backups' teal (a real collision the brief
 * explicitly asked to avoid).
 */
export type AccentTone =
  | 'neutral'
  | 'security'
  | 'automod'
  | 'moderation'
  | 'community'
  | 'automation'
  | 'ai'
  | 'tickets'
  | 'analytics'
  | 'settings'
  | 'logs'
  | 'backups'
  | 'help'
  | 'management';

export interface NavSection {
  /** Path segment under /dashboard/:guildId/ — '' means the index route. */
  path: string;
  label: string;
  icon: LucideIcon;
  accent: AccentTone;
  /** Shown in the 5-slot mobile bottom bar; the rest live under "More". */
  primaryMobile?: boolean;
}

/**
 * The exact 13-section structure from docs/DASHBOARD_INFORMATION_ARCHITECTURE.md.
 * Each section now carries its own accent (Batch 5) rather than several
 * sections sharing one collapsed tone.
 */
export const dashboardSections: NavSection[] = [
  { path: '', label: 'Overview', icon: LayoutDashboard, accent: 'neutral', primaryMobile: true },
  { path: 'security', label: 'Security', icon: ShieldAlert, accent: 'security' },
  { path: 'automod', label: 'AutoMod', icon: ShieldCheck, accent: 'automod' },
  {
    path: 'moderation',
    label: 'Moderation',
    icon: Scale,
    accent: 'moderation',
    primaryMobile: true,
  },
  {
    path: 'community',
    label: 'Community',
    icon: Users,
    accent: 'community',
    primaryMobile: true,
  },
  { path: 'automation', label: 'Automation', icon: Zap, accent: 'automation' },
  { path: 'ai', label: 'Dem AI', icon: Sparkles, accent: 'ai' },
  { path: 'tickets', label: 'Tickets', icon: Ticket, accent: 'tickets' },
  {
    path: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    accent: 'analytics',
    primaryMobile: true,
  },
  { path: 'settings', label: 'Server Configuration', icon: Settings, accent: 'settings' },
  { path: 'logs', label: 'Logs', icon: ScrollText, accent: 'logs' },
  { path: 'backups', label: 'Backups', icon: Archive, accent: 'backups' },
];
